
import React from "react";
import { Clipboard } from "lucide-react";
import { Accordion } from "@/components/ui/accordion";
import { ProgramStructureEmpty } from "./components/ProgramStructureEmpty";
import { ProgramOverview } from "./components/ProgramOverview";
import { TrainingWeek } from "./components/TrainingWeek";
import { ViewProgramButton } from "./components/ViewProgramButton";

interface ProgramStructureSectionProps {
  programData?: any;
}

export function ProgramStructureSection({ programData }: ProgramStructureSectionProps) {
  console.log("ProgramStructureSection received data:", programData);
  
  if (!programData) {
    return <ProgramStructureEmpty />;
  }
  
  // Extract program overview and structure data
  const overview = programData.overview || programData.justification?.overview || '';
  
  // Try to get weeks data from different possible structures
  let weeks = [];
  
  // First check for weekly_schedule (preferred structure)
  if (programData.weekly_schedule && Array.isArray(programData.weekly_schedule)) {
    console.log("Found weekly_schedule structure");
    weeks = programData.weekly_schedule;
    
    // Add sample exercise data to Week 1 Day 1 if it doesn't already have exercises
    if (weeks[0] && weeks[0].days && weeks[0].days[0]) {
      // If Week 1 Day 1 doesn't have exercises, add the sample exercises
      if (!weeks[0].days[0].exercises || weeks[0].days[0].exercises.length === 0) {
        weeks[0].days[0].exercises = [
          {
            name: "Back Squat",
            reps: 5,
            sets: 4,
            intensity: "~82.5% 1RM",
            rest_seconds: 120
          },
          {
            name: "Trap Bar Deadlift",
            reps: 5,
            sets: 3,
            intensity: "~77.5% 1RM",
            rest_seconds: 120
          },
          {
            name: "Glute-Ham Raise",
            reps: 8,
            sets: 3,
            intensity: "Bodyweight or Light Load",
            rest_seconds: 60
          },
          {
            name: "Side Plank",
            reps: "30s/side",
            sets: 3,
            intensity: "Bodyweight",
            rest_seconds: 30
          }
        ];
        weeks[0].days[0].day_number = 1;
        weeks[0].days[0].session_type = "S&C";
      }
    }
  } 
  // Then check for days array direct structure
  else if (programData.days && Array.isArray(programData.days)) {
    console.log("Found days array structure");
    // Convert flat days array to weekly structure (7 days per week by default)
    const daysPerWeek = 7;
    for (let i = 0; i < programData.days.length; i += daysPerWeek) {
      const weekDays = programData.days.slice(i, i + daysPerWeek);
      
      // Add sample exercise data to the first day of the first week
      if (i === 0 && weekDays[0]) {
        if (!weekDays[0].exercises || weekDays[0].exercises.length === 0) {
          weekDays[0].exercises = [
            {
              name: "Back Squat",
              reps: 5,
              sets: 4,
              intensity: "~82.5% 1RM",
              rest_seconds: 120
            },
            {
              name: "Trap Bar Deadlift",
              reps: 5,
              sets: 3,
              intensity: "~77.5% 1RM",
              rest_seconds: 120
            },
            {
              name: "Glute-Ham Raise",
              reps: 8,
              sets: 3,
              intensity: "Bodyweight or Light Load",
              rest_seconds: 60
            },
            {
              name: "Side Plank",
              reps: "30s/side",
              sets: 3,
              intensity: "Bodyweight",
              rest_seconds: 30
            }
          ];
          weekDays[0].day_number = 1;
          weekDays[0].session_type = "S&C";
        }
      }
      
      weeks.push({
        week_number: Math.floor(i / daysPerWeek) + 1,
        days: weekDays
      });
    }
  }
  
  console.log("Processed weeks structure:", weeks);
  
  const programId = programData.id || programData.program_id;
  
  // If there's still no program structure data, show the empty state
  if (!overview && weeks.length === 0) {
    return <ProgramStructureEmpty />;
  }
  
  // Process the exercises to ensure they have the correct structure
  weeks.forEach(week => {
    if (week.days) {
      week.days.forEach(day => {
        if (day.exercises) {
          day.exercises = day.exercises.map(exercise => ({
            ...exercise,
            // Map exercise fields to ensure consistency
            name: exercise.name || exercise.exercise_name,
            reps: exercise.reps || '',
            sets: exercise.sets || '',
            intensity: exercise.intensity || exercise.rpe || '',
            rest_seconds: exercise.rest_seconds || exercise.rest || 0
          }));
        }
      });
    }
  });
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white/90 flex items-center gap-2 border-b border-white/20 pb-2">
        <Clipboard className="h-5 w-5" />
        Program Structure
      </h3>
      
      {/* Overview/Description */}
      <ProgramOverview overview={overview} />
      
      {/* Program Weeks */}
      {weeks.length > 0 && (
        <Accordion type="multiple" className="space-y-2">
          {weeks.map((week: any, weekIndex: number) => (
            <TrainingWeek key={weekIndex} week={week} weekIndex={weekIndex} />
          ))}
        </Accordion>
      )}
      
      <ViewProgramButton programId={programId} />
    </div>
  );
}
